import type { WorldInit } from './initializeWorld';
import type { InputFrame, Pose } from '../controls/types';
import type { TourId } from '../tours/types';
import { createTourPlayer } from '../tours/tourPlayer';
import { createTourRoutes } from '../tours/routes';
import { createTourBoat } from '../tours/tourBoat';
import { createTourControls } from '../ui/tourControls';
import { tourInput } from '../tours/tourVisitor';

export function createTourRuntime(
  init: WorldInit,
  applyPose: (pose: Pose, mode: 'walk' | 'fly') => void,
  releaseInput: () => void,
  handoff: () => void,
  blocked: () => boolean,
  openGateway: () => void,
  enterCity: () => void,
  continueCity: () => void
) {
  const crowd = init.world.lifePlan.crowd;
  const routes = createTourRoutes(
    init.data.layout,
    crowd ? { crowd, collision: init.world.collision, obstacles: init.world.obstacles } : undefined
  );
  const player = createTourPlayer(routes, (p) => init.env.groundHeightAt(p));
  const boat = createTourBoat(init.data.palette, init.materials);
  init.scene.add(boat.root);

  function apply(pose: Pose | null): void {
    if (pose && player.state) {
      applyPose(pose, player.state.kind === 'walk' ? 'walk' : 'fly');
      boat.update(pose, player.state.bearing, player.state.kind === 'boat');
    }
    controls.update(player.state);
  }

  function start(id: TourId): void {
    enterCity();
    releaseInput();
    apply(player.start(id));
  }

  function stop(): void {
    if (player.state?.kind === 'boat') handoff();
    player.stop();
    boat.root.visible = false;
    controls.update(null);
  }

  const controls = createTourControls(routes, {
    start,
    pause: () => {
      player.togglePause();
      apply(player.update(0, { lookDx: 0, lookDy: 0 }));
      if (!player.state?.paused) continueCity();
    },
    restart: () => {
      apply(player.restart());
      if (!player.state?.paused) continueCity();
    },
    seek: (seconds) => {
      apply(player.seek(seconds));
    },
    speed: (rate) => {
      player.setRate(rate);
      controls.update(player.state);
    },
    stop: () => {
      stop();
      continueCity();
    },
    blocked,
    openGateway,
  });

  function update(frame: InputFrame, dt: number, suspended: boolean): boolean {
    if (!player.state) return false;
    apply(player.update(suspended ? 0 : dt, suspended ? { lookDx: 0, lookDy: 0 } : frame));
    return true;
  }

  return {
    input: (frame: InputFrame) => (player.state ? tourInput(frame) : frame),
    kind: () => player.state?.kind ?? null,
    state: () => player.state,
    mount: controls.mount,
    setVisible: controls.setVisible,
    start,
    stop,
    update,
    dispose: () => {
      controls.dispose();
      boat.dispose();
    },
  };
}
